/**
 * /admin/apps/:slug — the admin view of one app. No "Open app" button — to
 * use the app, click "Add me…" first which audits the self-grant and lets
 * the admin reach it via the regular Apps flow.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client.js';
import type { AdminAppDetailResponse } from '../../api/types.js';
import { AdminTabs } from '../../components/AdminTabs.js';
import {
  IconAlert,
  IconBolt,
  IconCheck,
  IconGlobe,
  IconLock,
  IconPlus,
  IconShield,
  IconTrash,
} from '../../components/Icons.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  Dialog,
  Mono,
  PageHeader,
  PageSkeleton,
  inputClass,
} from '../../components/ui.js';
import { relTime } from '../../lib/format.js';

type Role = 'manager' | 'admin' | 'member';

interface State {
  loading: boolean;
  data?: AdminAppDetailResponse;
  error?: string;
}

export function AdminAppDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ loading: true });
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    tone: 'info' | 'success' | 'error';
    text: string;
  } | null>(null);
  const [grantRole, setGrantRole] = useState<Role>('member');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    if (!slug) return;
    setState({ loading: true });
    try {
      const data = await api.get<AdminAppDetailResponse>(
        `/api/admin/apps/${encodeURIComponent(slug)}`,
      );
      setState({ loading: false, data });
    } catch (err) {
      setState({
        loading: false,
        error: err instanceof ApiError ? err.message : String(err),
      });
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  async function disable() {
    if (!slug) return;
    if (
      !confirm(
        `Disable "${slug}"? The live and preview URLs stop serving traffic immediately and the app is hidden from everyone except admins. Re-enable to restore.`,
      )
    )
      return;
    setBusy('disable');
    setMessage(null);
    try {
      await api.post(`/api/admin/apps/${encodeURIComponent(slug)}/disable`);
      setMessage({ tone: 'success', text: `Disabled "${slug}".` });
      await load();
    } catch (err) {
      setMessage({
        tone: 'error',
        text: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setBusy(null);
    }
  }

  async function enable() {
    if (!slug) return;
    setBusy('enable');
    setMessage(null);
    try {
      await api.post(`/api/admin/apps/${encodeURIComponent(slug)}/enable`);
      setMessage({ tone: 'success', text: `Re-enabled "${slug}".` });
      await load();
    } catch (err) {
      setMessage({
        tone: 'error',
        text: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setBusy(null);
    }
  }

  async function selfGrant() {
    if (!slug) return;
    setBusy('self-grant');
    setMessage(null);
    try {
      await api.post(`/api/admin/apps/${encodeURIComponent(slug)}/self-grant`, {
        role: grantRole,
      });
      setMessage({
        tone: 'success',
        text:
          grantRole === 'manager'
            ? `You are now a manager of "${slug}". Audit log captured the action.`
            : `You now hold the ${grantRole} app-role on "${slug}". Audit log captured the action.`,
      });
      await load();
    } catch (err) {
      setMessage({
        tone: 'error',
        text: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setBusy(null);
    }
  }

  async function handleConfirmedDelete() {
    if (!slug) return;
    setBusy('delete');
    try {
      await api.delete(`/api/admin/apps/${encodeURIComponent(slug)}`, { confirm_slug: slug });
      navigate('/admin/apps', { replace: true });
    } catch (err) {
      setMessage({
        tone: 'error',
        text: err instanceof ApiError ? err.message : String(err),
      });
      setBusy(null);
      setDeleteOpen(false);
    }
  }

  if (state.loading) return <PageSkeleton rows={5} />;
  if (state.error) return <Banner tone="error">{state.error}</Banner>;
  const data = state.data;
  if (!data) return null;
  const { app, managers, roles, caller } = data;
  const isFrozen = app.status === 'frozen';

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Admin / Apps"
        back={{ to: '/admin/apps', label: 'All apps' }}
        title={
          <span className="inline-flex items-center gap-3 flex-wrap">
            {app.name}
            <StatusBadge status={app.status} />
            <span
              className={
                'inline-flex items-center gap-1 text-xs font-medium ' +
                (app.visibility === 'workspace' ? 'text-info' : 'text-muted')
              }
            >
              {app.visibility === 'workspace' ? (
                <>
                  <IconGlobe size={12} /> Mytra-wide
                </>
              ) : (
                <>
                  <IconLock size={12} /> Private
                </>
              )}
            </span>
          </span>
        }
        description={app.description || <Mono>{app.slug}</Mono>}
      />

      <AdminTabs />

      {message && (
        <div className="mb-5">
          <Banner tone={message.tone} onDismiss={() => setMessage(null)}>
            {message.text}
          </Banner>
        </div>
      )}

      <Banner tone="info">
        <span className="inline-flex items-center gap-1.5">
          <IconShield size={14} />
          You&apos;re viewing this app as a Kin admin. To open and use the live app, grant yourself
          an app-role below — every action here is captured in the audit log.
        </span>
      </Banner>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        {/* Metadata */}
        <Card>
          <CardHeader title="Metadata" />
          <dl className="text-sm">
            <Field label="Slug" value={<Mono>{app.slug}</Mono>} />
            <Field label="Region" value={app.region} />
            <Field
              label="Live URL"
              value={app.cf_custom_domain_live ? <Mono>{app.cf_custom_domain_live}</Mono> : '—'}
            />
            <Field
              label="Preview URL"
              value={
                app.cf_custom_domain_preview ? <Mono>{app.cf_custom_domain_preview}</Mono> : '—'
              }
            />
            <Field
              label="Worker"
              value={app.cf_worker_name ? <Mono>{app.cf_worker_name}</Mono> : '—'}
            />
            <Field label="D1 (live)" value={app.cf_d1_id ? <Mono>{app.cf_d1_id}</Mono> : '—'} />
            <Field label="Created" value={relTime(app.created_at)} />
            <Field label="Updated" value={relTime(app.updated_at)} />
          </dl>
        </Card>

        {/* Admin actions */}
        <Card>
          <CardHeader
            title="Admin actions"
            description="Every action here writes to the audit log."
          />
          <div className="px-5 py-5 space-y-4">
            {/* Disable / Enable */}
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm">
                <p className="font-medium text-ink">{isFrozen ? 'Re-enable app' : 'Disable app'}</p>
                <p className="text-muted text-xs mt-0.5">
                  {isFrozen
                    ? 'Reattaches the Worker routes so the live + preview URLs serve again, and makes the app visible to its users.'
                    : 'Detaches the Cloudflare Worker routes — the URLs stop serving immediately — and hides the app from everyone except admins.'}
                </p>
              </div>
              {isFrozen ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void enable()}
                  loading={busy === 'enable'}
                  disabled={busy !== null}
                >
                  <IconCheck size={13} />
                  Re-enable
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void disable()}
                  loading={busy === 'disable'}
                  disabled={busy !== null}
                >
                  <IconBolt size={13} />
                  Disable
                </Button>
              )}
            </div>

            {/* Self-grant */}
            <div className="border-t border-line pt-4">
              <p className="font-medium text-ink text-sm">Grant yourself access</p>
              <p className="text-muted text-xs mt-0.5 mb-3">
                {caller.is_manager || caller.app_role
                  ? 'You already have access. Granting again upgrades or duplicates the row (no-op).'
                  : "Pick an axis. Manager = platform power (deploy, settings). Admin / Member = the app's own runtime role."}
              </p>
              <div className="flex items-center gap-2">
                <select
                  value={grantRole}
                  onChange={(e) => setGrantRole(e.target.value as Role)}
                  aria-label="Role"
                  className={`${inputClass} !w-auto !py-2`}
                >
                  <option value="member">Member (app role)</option>
                  <option value="admin">Admin (app role)</option>
                  <option value="manager">Manager (platform power)</option>
                </select>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => void selfGrant()}
                  loading={busy === 'self-grant'}
                  disabled={busy !== null}
                >
                  <IconPlus size={13} />
                  Add me
                </Button>
              </div>
              {(caller.is_manager || caller.app_role) && (
                <p className="text-xs text-muted mt-2">
                  Current: {caller.is_manager && <span className="text-brand">manager</span>}
                  {caller.is_manager && caller.app_role && ' · '}
                  {caller.app_role && (
                    <span className="text-brand">{caller.app_role} (app role)</span>
                  )}
                </p>
              )}
            </div>

            {/* Hard delete */}
            <div className="border-t border-line pt-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm">
                  <p className="font-medium text-danger inline-flex items-center gap-1.5">
                    <IconAlert size={14} />
                    Hard delete
                  </p>
                  <p className="text-muted text-xs mt-0.5">
                    Tears down Workers, both D1s, R2 — permanent. Audit log captures it.
                  </p>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setDeleteOpen(true)}
                  disabled={busy !== null}
                >
                  <IconTrash size={13} />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Managers + role grants */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <Card>
          <CardHeader title="Managers" description={`${managers.length} total`} />
          {managers.length === 0 ? (
            <p className="px-5 py-5 text-muted text-sm">No managers.</p>
          ) : (
            <ul className="divide-y divide-line">
              {managers.map((m) => (
                <li
                  key={m.user_id}
                  className="px-5 py-2.5 text-sm flex items-center justify-between"
                >
                  <span className="min-w-0 truncate">
                    <span className="font-medium text-ink">{m.name ?? m.email}</span>
                    {m.name && <span className="text-muted ml-2 text-xs">{m.email}</span>}
                  </span>
                  <span className="text-xs text-faint">{relTime(m.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader title="App-role grants" description={`${roles.length} total`} />
          {roles.length === 0 ? (
            <p className="px-5 py-5 text-muted text-sm">No app-role grants.</p>
          ) : (
            <ul className="divide-y divide-line">
              {roles.map((r) => (
                <li
                  key={r.user_id}
                  className="px-5 py-2.5 text-sm flex items-center justify-between"
                >
                  <span className="min-w-0 truncate">
                    <span className="font-medium text-ink">{r.name ?? r.email}</span>
                    {r.name && <span className="text-muted ml-2 text-xs">{r.email}</span>}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-brand-soft text-brand font-medium uppercase tracking-wide">
                    {r.role}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {deleteOpen && (
        <DeleteDialog
          slug={app.slug}
          appName={app.name}
          busy={busy === 'delete'}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={handleConfirmedDelete}
        />
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-x-4 py-2 px-5 border-b border-line last:border-b-0">
      <dt className="text-xs uppercase tracking-wide text-muted font-medium pt-1">{label}</dt>
      <dd className="text-sm text-ink truncate">{value}</dd>
    </div>
  );
}

function DeleteDialog({
  slug,
  appName,
  busy,
  onCancel,
  onConfirm,
}: {
  slug: string;
  appName: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const matches = typed === slug;

  return (
    <Dialog
      title={
        <span className="inline-flex items-center gap-2 text-danger">
          <IconAlert size={18} />
          Delete &quot;{appName}&quot;?
        </span>
      }
      busy={busy}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="md"
            onClick={onConfirm}
            loading={busy}
            disabled={!matches || busy}
          >
            Delete permanently
          </Button>
        </>
      }
    >
      <p>
        This permanently deletes the app and all of its data: the Worker, both databases (preview +
        live), every asset, every source archive. Audited.{' '}
        <span className="font-semibold text-ink">This cannot be undone.</span>
      </p>
      <p className="text-muted">
        To confirm, type the slug{' '}
        <code className="font-mono bg-surface-2 px-1.5 py-0.5 rounded text-ink">{slug}</code> below.
      </p>
      <input
        ref={inputRef}
        type="text"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        placeholder={slug}
        disabled={busy}
        className={`${inputClass} font-mono`}
      />
    </Dialog>
  );
}
